'use client'

import _ from 'lodash'
import { useParams } from 'next/navigation'
import { useQuery } from "@tanstack/react-query"
import slugify from 'slugify'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import NextLink from 'next/link'

import { Avatar, Bullet } from '@/components'
import { Cruz } from '@cruz-com/lookup'
import { pages } from '@/settings'
import { userApi } from '@/api'

dayjs.extend(relativeTime)

type Props = {
  initialData: Cruz.UserDisplay | Cruz.User
  position?: 'top' | 'bottom'
}

export function NavUserTop({ initialData }: Props) {
  const id = initialData?._id || useParams()?.id
  const { data }: { data: Cruz.UserDisplay | Cruz.User } = useQuery({
    queryKey: [`UserProfile_${id}`],
    queryFn: () => userApi.getProfile(id as string),
    staleTime: 3000,
    initialData,
  })

  const { _id, first_name, last_name } = data || {}

  const NavItem = ({ name, href, count }: { name: string, href: string, count?: number }) => {
    if (_.isEmpty(data)) {
      return <></>
    }
    if (_.isUndefined(count) || count === 0) {
      return <></>
    }
    href = href.replace('[id]', _id).replace('[name]', slugify(first_name + ' ' + last_name))
    return (
      <li className='py-1.5'>
        <NextLink hrefLang='en' href={href} className='nav-item border'>
          {name}
          <span className='nav-count'>{count}</span>
        </NextLink>
      </li>
    )
  }

  return (
    <div className='block lg:hidden'>
      <div className='w-full flex items-top px-4 lg:px-0'>
        <Avatar className='size-16 sm:size-20' photo_url={data?.photo_url} name={`${first_name} ${last_name}`} />
        <div className='pl-6'>
          <div className='text-lg font-bold truncate'>
            {first_name} {last_name}
          </div>
          <div className='text-sm font-light truncate flex flex-col sm:flex-row'>
            <span>{data?.location?.city}, {data?.location?.state_abbr} {data?.location?.zip_code}</span>
            <Bullet className='px-2 hidden sm:block' />
            <span>Joined<span></span> {dayjs(data?.date_create).fromNow()}</span>
          </div>
          <ul role='list' className='flex items-center -ml-2 gap-x-2 mt-2'>
            <NavItem {...pages.userVehicles} count={data?.statistic?.vehicle_count || 0} />
            <NavItem {...pages.userSpotted} count={data?.statistic?.spotted_count || 0} />
          </ul>
        </div>
      </div>
      <div className='mb-4 pb-4 border-b-4 border-zinc-100 dark:border-zinc-800' />
    </div>
  )
}

